import spacetime from 'spacetime';

import { settings } from '../store/settings.store';
import { schedule } from '../store/schedule.store';
import { customer } from '../store/customer.store';
import { selection } from '../store/selection.store';

import { openAccountFlow } from '../modals/account.modal';
import { showMessage } from '../modals/message.modal';
import { openSelectionDialog } from '../modals/selection.modal';

import { formatDuration } from './utils';

import { ScheduleCalendarSpaceIncrement } from '@artask/tambr-models/models/schedule.model';
import { Order } from '@artask/tambr-models/models/orders.model';



export function incrementClicked( spaceId: string, slotTs: number ) {
  if ( !!settings.demo ) return;
  if ( document.getElementsByTagName('tambr-calendar-selection').length ) return;
  if ( isIncrementBookedByCurrentUser( spaceId, slotTs, customer.customerOrders ) ) openAccountFlow();
  if ( !isIncrementAvailable( spaceId, slotTs ) ) return;

  const scheduleSettings = settings.businessSettings.scheduleSettings;
  const scheduleIncrement = scheduleSettings.scheduleIncrement;
  const slotDuration = scheduleSettings.slotDuration;
  const incrementsPerSlot = slotDuration / scheduleIncrement;    
  const minLimitEnabled = scheduleSettings?.bookingDurationMin?.enabled;
  const minLimit = minLimitEnabled ? scheduleSettings.bookingDurationMin.value : 1;
  const increments = [];

  // check if all required increments are available
  for ( let i = 0; i < incrementsPerSlot * minLimit; i++ ) {
    const checkedIncrement = slotTs + ( i * scheduleIncrement * 60 * 1000 );
    if ( isIncrementAvailable( spaceId, checkedIncrement ) ) { 
      increments.push( checkedIncrement );
    } else {
      const minDuration = formatDuration( minLimit * slotDuration );
      return showMessage( `The minimum session duration is ${minDuration}.`, 'This time slot is not available' );
    }
  }

  selection.spaceId = spaceId;
  selection.increments = increments;
  selection.duration = incrementsPerSlot * minLimit * scheduleIncrement;

  openSelectionDialog();
}


export function getIncrement( spaceId: string, incrementTimestamp: number ): ScheduleCalendarSpaceIncrement {
  return schedule.dateSchedule.calendar[ spaceId ].increments[ incrementTimestamp ];
}


export function isIncrementAvailable( spaceId: string, slotTs: number ): boolean {
  const increment = getIncrement( spaceId, slotTs );
  return !!increment && increment.available && !increment.expired;
}


export function isIncrementPast( spaceId: string, slotTs: number ): boolean {
  const increment = getIncrement( spaceId, slotTs );
  return !!increment && increment.expired;
}


export function isIncrementBookedByCurrentUser( 
  spaceId: string,
  slotTs: number,
  customerOrders: Order[],
): boolean {
  const tz = settings.businessSettings.timezone;
  return customerOrders?.some( (order: Order) => {
    if ( order.space.id !== spaceId ) return false;
    const orderStart = spacetime( order.startTime.toMillis(), tz );
    const orderEnd = spacetime( order.endTime.toMillis(), tz ).subtract( 1, 'minute' );
    return !spacetime( slotTs, tz ).isBefore( orderStart ) && !spacetime( slotTs, tz ).isAfter( orderEnd );
  } );
}


export function isIncrementSelectable( spaceId: string, slotTs: number ): boolean {
  if ( !isIncrementAvailable( spaceId, slotTs ) ) return;
  const minLimitEnabled = settings.businessSettings.scheduleSettings?.bookingDurationMin?.enabled;
  const minLimit = minLimitEnabled ? settings.businessSettings.scheduleSettings?.bookingDurationMin.value : 1;
  const slotDuration = schedule.dateSchedule.slotDuration;
  const scheduleIncrement = settings.businessSettings.scheduleSettings.scheduleIncrement;
  const incrementsPerSlot = slotDuration / scheduleIncrement;
  for ( let i = 0; i < incrementsPerSlot * minLimit; i++ ) {
    const checkedIncrement = slotTs + ( i * scheduleIncrement * 60 * 1000 );
    if ( !isIncrementAvailable( spaceId, checkedIncrement ) ) {
      return false;
    } else {
      continue;
    }
  }
  return true;
}


export function getIncrementClasses( spaceId: string, slotTs: number ): string {
  let classes = [ 'slot' ];
  classes.push( isIncrementAvailable( spaceId, slotTs ) ? 'available': 'not-available' );
  classes.push( isIncrementSelectable( spaceId, slotTs ) ? 'selectable' : 'not-selectable' );
  if ( isIncrementBookedByCurrentUser( spaceId, slotTs, customer.customerOrders ) ) classes.push( 'current-user-order' );
  return classes.join(' ');
}